import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import Portal from "@mui/material/Portal";
import Fab from "@mui/material/Fab";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";

/**
 * Shared mobile filters panel: Drawer + FAB.
 * Pass container to render the Drawer inside that node (e.g. inside a Dialog); otherwise portals to document.body.
 * When container is provided, body scroll lock is skipped (parent owns scroll). Manages viewport position/size and scroll restore.
 */
export function MobileFiltersPanel({
  open,
  onClose,
  onOpenClick,
  children,
  badgeCount = 0,
  dataTestId = "filters-drawer",
  zIndex = 1400,
  scrollRestoreDeps = [],
  container = null,
  portalToBody = false,
}) {
  const mainScrollWhenOpenedRef = useRef(0);
  const [drawerTop, setDrawerTop] = useState(0);
  const [drawerHeight, setDrawerHeight] = useState("100vh");

  // When drawer is open and not inside a custom container, lock body scroll so panel scroll doesn't pull the rest of the layout
  useEffect(() => {
    if (!open || container) return;
    const scrollY = window.scrollY;
    mainScrollWhenOpenedRef.current = scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      // Always reset to default so the view is scrollable again (avoids leaving body stuck if MUI or another lock touched it)
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      // Restore to ref (may have been clamped when list shrank while drawer was open)
      window.scrollTo(0, mainScrollWhenOpenedRef.current);
    };
  }, [open, container]);

  // When drawer opens, save scroll and set size/position from visible viewport
  useEffect(() => {
    if (open) {
      mainScrollWhenOpenedRef.current = window.scrollY;
      if (window.visualViewport) {
        const vv = window.visualViewport;
        setDrawerTop(vv.offsetTop);
        setDrawerHeight(`${vv.height}px`);
      } else {
        setDrawerTop(0);
        setDrawerHeight("100vh");
      }
    }
  }, [open]);

  // When keyboard/chrome changes with drawer open: update position/size and restore scroll. Only "resize" (not "scroll") so dragging the panel doesn't trigger updates and jitter.
  useEffect(() => {
    if (!open || !window.visualViewport) return;
    const vv = window.visualViewport;
    const stabilize = () => {
      setDrawerTop(vv.offsetTop);
      setDrawerHeight(`${vv.height}px`);
      window.scrollTo(0, mainScrollWhenOpenedRef.current);
    };
    vv.addEventListener("resize", stabilize);
    return () => vv.removeEventListener("resize", stabilize);
  }, [open]);

  // When list shrinks (e.g. search) while drawer open, clamp scroll to valid range so content isn't left way above the view
  useEffect(() => {
    if (!open || scrollRestoreDeps.length === 0) return;
    const saved = mainScrollWhenOpenedRef.current;
    const raf = requestAnimationFrame(() => {
      const maxScroll = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight,
      );
      const clampedScroll = Math.min(saved, maxScroll);
      // While drawer is open body is fixed; update body.top so the visible scroll is clamped
      if (document.body.style.position === "fixed") {
        document.body.style.top = `-${clampedScroll}px`;
      }
      mainScrollWhenOpenedRef.current = clampedScroll;
      window.scrollTo(0, clampedScroll);
    });
    return () => cancelAnimationFrame(raf);
  }, [open, scrollRestoreDeps]);

  // Enter key closes drawer (same as Done: blur then close)
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        if (document.activeElement?.blur) document.activeElement.blur();
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const content = (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{ zIndex }}
        ModalProps={{
          disableScrollLock: true,
          container: container ?? (typeof document !== "undefined" ? document.body : undefined),
          BackdropProps: {
            sx: {
              top: drawerTop,
              height: drawerHeight,
              maxHeight: "100vh",
              touchAction: "none",
            },
            onTouchMove: (e) => e.preventDefault(),
          },
        }}
        PaperProps={{
          sx: {
            top: drawerTop,
            width: 320,
            maxWidth: "100%",
            height: drawerHeight,
            maxHeight: "100vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
          onTouchStart: (e) => e.stopPropagation(),
          onTouchMove: (e) => e.stopPropagation(),
        }}
        data-testid={dataTestId}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
            position: "relative",
            isolation: "isolate",
          }}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h6">Filters</Typography>
            <IconButton
              aria-label="Close filters"
              onClick={onClose}
              data-testid={`close-${dataTestId}`}
            >
              <ClearIcon />
            </IconButton>
          </Box>
          {children}
        </Box>
        </Box>
      </Drawer>

      <Fab
        color="primary"
        aria-label="Open filters"
        onClick={onOpenClick}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 16,
          zIndex: zIndex - 1,
        }}
        data-testid={`open-${dataTestId}`}
      >
        <Badge
          badgeContent={badgeCount}
          color="secondary"
          invisible={badgeCount === 0}
        >
          <FilterListIcon />
        </Badge>
      </Fab>
    </>
  );

  if (portalToBody && typeof document !== "undefined") {
    return <Portal container={document.body}>{content}</Portal>;
  }
  return content;
}
